import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandKitProvider } from "@/lib/brand";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Join from "@/pages/Join";
import DashboardLayout from "@/pages/DashboardLayout";
import Overview from "@/pages/Overview";
import Today from "@/pages/Today";
import DataSources from "@/pages/DataSources";
import DataSourceDetail from "@/pages/DataSourceDetail";
import Insights from "@/pages/Insights";
import Monetization from "@/pages/Monetization";
import Assistant from "@/pages/Assistant";
import Settings from "@/pages/Settings";
import AdminCalls from "@/pages/AdminCalls";
import AdminSystem from "@/pages/AdminSystem";
import BillingSuccess from "@/pages/BillingSuccess";
import WalkthroughShare from "@/pages/WalkthroughShare";
import WalkthroughEmbed from "@/pages/WalkthroughEmbed";
import Walkthroughs from "@/pages/Walkthroughs";
import ABMPage from "@/pages/ABMPage";
import ABMAdmin from "@/pages/ABMAdmin";
import DealRoomPage from "@/pages/DealRoomPage";
import DealRoomsAdmin from "@/pages/DealRoomsAdmin";
import CashFlow from "@/pages/CashFlow";
import XeroHygiene from "@/pages/XeroHygiene";
import Commodities from "@/pages/Commodities";
import HedgeLedger from "@/pages/HedgeLedger";
import Industrial from "@/pages/Industrial";
import IndustrialConnector from "@/pages/IndustrialConnector";
import IndustrialConnectorDemo from "@/pages/IndustrialConnectorDemo";
import AIBenchmark from "@/pages/AIBenchmark";
import Ventia from "@/pages/Ventia";
import Ventia7SectorDemo from "@/pages/Ventia7SectorDemo";
import Dabble from "@/pages/Dabble";
import DabbleLive from "@/pages/DabbleLive";
import Wagering from "@/pages/Wagering";
import WhyDara from "@/pages/WhyDara";
import PremierCompliance from "@/pages/PremierCompliance";
import Dayo from "@/pages/Dayo";
import Burditt from "@/pages/Burditt";
import PosLive from "@/pages/PosLive";
import ExplorationTrial from "@/pages/ExplorationTrial";
import NGOIntelligence from "@/pages/NGOIntelligence";
import AITrainingData from "@/pages/AITrainingData";
import IWG from "@/pages/IWG";
import StrategicPlanningInstitute from "@/pages/StrategicPlanningInstitute";
import StickyCTABar from "@/components/StickyCTABar";
import ExitIntentModal from "@/components/ExitIntentModal";
import ChatbotWidget from "@/components/ChatbotWidget";
import FoundersGuide from "@/pages/FoundersGuide";
import PitchLinksAdmin from "@/pages/PitchLinksAdmin";
import PitchView from "@/pages/PitchView";
import IndustryLanding from "@/pages/IndustryLanding";
import AdminLeads from "@/pages/AdminLeads";
import AdminVisitors from "@/pages/AdminVisitors";
import POSLive from "@/pages/POSLive";
import SalesTracker from "@/pages/SalesTracker";
import AdminCaseStudies from "@/pages/AdminCaseStudies";
import AdminPulseWall from "@/pages/AdminPulseWall";
import PulseWall from "@/pages/PulseWall";
import RegulatorPackPreview from "@/pages/RegulatorPackPreview";
import FunnelAnalytics from "@/pages/FunnelAnalytics";
import AdminOutbound from "@/pages/AdminOutbound";
import { CaseStudiesIndex, CaseStudyDetail } from "@/pages/CaseStudies";
import Security from "@/pages/Security";
import Consent from "@/pages/Consent";
import Pricing from "@/pages/Pricing";
import Founding from "@/pages/Founding";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Experts from "@/pages/Experts";
import ExpertsApply from "@/pages/ExpertsApply";
import SignAgreement from "@/pages/SignAgreement";
import AnalystDashboard from "@/pages/AnalystDashboard";
import AdminAnalysts from "@/pages/AdminAnalysts";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import MobileGate from "@/components/MobileGate";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-black text-zinc-500 font-mono flex items-center justify-center text-xs">LOADING…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrandKitProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/join/:code" element={<Join />} />
          <Route path="/share/walkthrough/:id" element={<WalkthroughShare />} />
          <Route path="/embed/walkthrough/:id" element={<WalkthroughEmbed />} />
          <Route path="/abm/:slug" element={<ABMPage />} />
          <Route path="/deal-room/:slug" element={<DealRoomPage />} />
          <Route path="/industrial" element={<Industrial />} />
          <Route path="/industrial/connector" element={<IndustrialConnector />} />
          <Route path="/industrial-demo" element={<IndustrialConnectorDemo />} />
          <Route path="/connector-demo" element={<IndustrialConnectorDemo />} />
          <Route path="/ai-maturity-benchmark" element={<AIBenchmark />} />
          <Route path="/ventia" element={<Ventia />} />
          <Route path="/ventia/demo" element={<Ventia7SectorDemo />} />
          <Route path="/dabble" element={<Dabble />} />
          <Route path="/dabble/live" element={<DabbleLive />} />
          <Route path="/wagering" element={<Wagering />} />
          <Route path="/why-dara" element={<WhyDara />} />
          <Route path="/premier-compliance" element={<PremierCompliance />} />
          <Route path="/dayo" element={<Dayo />} />
          <Route path="/burditt" element={<Burditt />} />
          <Route path="/pos-live" element={<PosLive />} />
          <Route path="/exploration-trial" element={<ExplorationTrial />} />
          <Route path="/ngo-intelligence" element={<NGOIntelligence />} />
          <Route path="/ngo" element={<Navigate to="/ngo-intelligence" replace />} />
          <Route path="/ai-training" element={<AITrainingData />} />
          <Route path="/training-data" element={<Navigate to="/ai-training" replace />} />
          <Route path="/iwg" element={<IWG />} />
          <Route path="/strategic-planning-institute" element={<StrategicPlanningInstitute />} />
          <Route path="/burditt" element={<StrategicPlanningInstitute />} />
          <Route path="/founders-guide" element={<FoundersGuide />} />
          <Route path="/industries/:slug" element={<IndustryLanding />} />
          <Route path="/case-studies" element={<CaseStudiesIndex />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
          <Route path="/wall" element={<PulseWall />} />
          <Route path="/regulator-pack-preview" element={<RegulatorPackPreview />} />
          <Route path="/security" element={<Security />} />
          <Route path="/consent" element={<Consent />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/founding" element={<Founding />} />
          <Route path="/rope-access" element={<Navigate to="/founding?vertical=rope-access" replace />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/experts" element={<Experts />} />
          <Route path="/experts/apply" element={<ExpertsApply />} />
          <Route path="/sign-agreement/:analystId" element={<SignAgreement />} />
          <Route path="/pitch/:token" element={<PitchView />} />
          <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
            <Route index element={<Overview />} />
            <Route path="today" element={<Today />} />
            <Route path="sources" element={<DataSources />} />
            <Route path="sources/:id" element={<MobileGate label="Data Source Detail"><DataSourceDetail /></MobileGate>} />
            <Route path="insights" element={<MobileGate label="DARA Insights"><Insights /></MobileGate>} />
            <Route path="monetization" element={<MobileGate label="Monetization Map"><Monetization /></MobileGate>} />
            <Route path="cashflow" element={<MobileGate label="Cash-Flow Forecast"><CashFlow /></MobileGate>} />
            <Route path="hygiene" element={<MobileGate label="Xero Hygiene"><XeroHygiene /></MobileGate>} />
            <Route path="commodities" element={<MobileGate label="Commodity Desk"><Commodities /></MobileGate>} />
            <Route path="hedge-ledger" element={<MobileGate label="Hedge Ledger"><HedgeLedger /></MobileGate>} />
            <Route path="pos" element={<POSLive />} />
            <Route path="admin/pitch-links" element={<MobileGate label="Pitch Links Admin"><PitchLinksAdmin /></MobileGate>} />
            <Route path="assistant" element={<Assistant />} />
            <Route path="walkthroughs" element={<MobileGate label="Walkthroughs"><Walkthroughs /></MobileGate>} />
            <Route path="abm" element={<MobileGate label="ABM Pages"><ABMAdmin /></MobileGate>} />
            <Route path="deal-rooms" element={<MobileGate label="Deal Rooms"><DealRoomsAdmin /></MobileGate>} />
            <Route path="settings" element={<Settings />} />
            <Route path="admin/calls" element={<MobileGate label="Sales Inbox"><AdminCalls /></MobileGate>} />
            <Route path="admin/system" element={<MobileGate label="System Admin"><AdminSystem /></MobileGate>} />
            <Route path="admin/leads" element={<MobileGate label="Leads Admin"><AdminLeads /></MobileGate>} />
            <Route path="admin/visitors" element={<MobileGate label="Visitors Admin"><AdminVisitors /></MobileGate>} />
            <Route path="admin/sales" element={<MobileGate label="Sales Pipeline"><SalesTracker /></MobileGate>} />
            <Route path="admin/case-studies" element={<MobileGate label="Case Studies Admin"><AdminCaseStudies /></MobileGate>} />
            <Route path="admin/wall" element={<MobileGate label="Pulse Wall Admin"><AdminPulseWall /></MobileGate>} />
            <Route path="admin/funnel" element={<MobileGate label="Funnel Analytics"><FunnelAnalytics /></MobileGate>} />
            <Route path="admin/outbound" element={<MobileGate label="Outbound Campaigns"><AdminOutbound /></MobileGate>} />
            <Route path="admin/analysts" element={<MobileGate label="Analyst Queue"><AdminAnalysts /></MobileGate>} />
            <Route path="analyst" element={<AnalystDashboard />} />
            <Route path="billing/success" element={<BillingSuccess />} />
          </Route>
        </Routes>
        <StickyCTABar />
        <ExitIntentModal />
        <ChatbotWidget />
        </BrowserRouter>
      </BrandKitProvider>
      <Toaster theme="dark" position="top-right" />
      <PWAInstallPrompt />
    </AuthProvider>
  );
}
